import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { download, get, patch, post } from '../../shared/apiClient';

export interface ProfileData {
  full_name: string;
  email: string;
  telephone: string | null;
  has_password: boolean;
}

export interface UpdateProfilePayload {
  full_name: string;
  email: string;
  telephone?: string | null;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface PaymentHistoryItem {
  payment_uuid: string;
  amount_cents: number;
  currency: string;
  status: string;
  created_at: string;
  receipt_url: string | null;
}

export interface DeclarationItem {
  session_uuid: string;
  tax_year: number | null;
  country: string | null;
  /** "in_progress" | "declaration_in_progress" | "presented" — resolved by the backend */
  status: string;
  started_at: string | null;
  completed_at: string | null;
}

export function useGetProfile(options?: { enabled?: boolean }) {
  return useQuery<ProfileData>({
    queryKey: ['settings', 'profile'],
    queryFn: () => get<ProfileData>('/settings/profile').then((r) => r.data),
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation<ProfileData, unknown, UpdateProfilePayload>({
    mutationFn: (data) =>
      patch<UpdateProfilePayload, ProfileData>('/settings/profile', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'profile'] });
    },
  });
}

export function useChangePassword() {
  return useMutation<unknown, unknown, ChangePasswordPayload>({
    mutationFn: (data) => post('/settings/change-password', data).then((r) => r.data),
  });
}

export function usePaymentHistory() {
  return useQuery<PaymentHistoryItem[]>({
    queryKey: ['settings', 'payments'],
    queryFn: () => get<PaymentHistoryItem[]>('/settings/payments').then((r) => r.data),
  });
}

export function useDeclarations() {
  return useQuery<DeclarationItem[]>({
    queryKey: ['settings', 'declarations'],
    queryFn: () => get<DeclarationItem[]>('/settings/declarations').then((r) => r.data),
  });
}

export interface DeclarationDocument {
  id: string;
  filename: string;
  label: string | null;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

export function useDeclarationDocuments(sessionUuid: string, enabled: boolean) {
  return useQuery<DeclarationDocument[]>({
    queryKey: ['settings', 'declaration-documents', sessionUuid],
    queryFn: () =>
      get<DeclarationDocument[]>(`/settings/declarations/${sessionUuid}/documents`).then(
        (r) => r.data,
      ),
    enabled,
  });
}

export async function downloadDeclarationDocument(
  docId: string,
  filename: string,
): Promise<void> {
  const blob = await download(`/settings/documents/${docId}/download`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadInvoice(paymentUuid: string): Promise<void> {
  const response = await get<string>(`/settings/payments/${paymentUuid}/invoice`);
  const html = response.data as string;
  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
  }
}